import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { COMPANY } from "../data/content";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: "Forside", path: "/" },
    { label: "Hovtrim", path: "/hovtrim" },
    { label: "FAQ", path: "/faq" },
    { label: "Om mig", path: "/om-mig" },
    { label: "Om uddannelsen ISNHCP", path: "/isnhcp" },
    { label: "Vandrefolde", path: "/vandrefolde" },
    { label: "Priser", path: "/priser" },
    { label: "Kontakt", path: "/kontakt" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-40 bg-sand-50/95 backdrop-blur-md border-b border-sand-200/80 shadow-2xs">
      {/* Top bar with contact details */}
      <div className="bg-forest-900 text-sand-100 py-2 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="hover:text-white transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <i className="fa-solid fa-phone text-emerald-400"></i>
              <span className="whitespace-nowrap font-medium">{COMPANY.phone}</span>
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="hidden sm:flex items-center gap-1.5 hover:text-white transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-envelope text-emerald-400"></i>
              <span className="whitespace-nowrap font-medium">{COMPANY.email}</span>
            </a>
            <span className="hidden md:inline-block text-sand-300/60">|</span>
            <span className="hidden md:inline-flex items-center gap-1.5 text-sand-200">
              <i className="fa-solid fa-location-dot text-amber-400"></i>
              {COMPANY.address}, {COMPANY.city}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <span className="hidden lg:inline-block text-sand-300/80">
              <i className="fa-solid fa-check text-emerald-400 mr-1"></i>
              Naturlig hovtrimning i hele Jylland
            </span>
            <a
              href={COMPANY.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Besøg os på Facebook"
              className="hover:text-white transition-colors text-sand-200 cursor-pointer flex items-center gap-1 text-xs"
            >
              <i className="fa-brands fa-facebook text-sm text-blue-300"></i>
              <span className="hidden sm:inline">Facebook</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div>
              <span className="text-2xl font-bold tracking-tight text-forest-900 block leading-none">
                HOOFCARE
              </span>
              <span className="text-[11px] font-medium tracking-wide text-earth-700 uppercase block mt-1">
                Naturlig Hovtrimning
              </span>
            </div>
          </Link>

          {/* Desktop menu: visible ONLY at >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3.5 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer relative ${
                  isActive(item.path)
                    ? "bg-forest-900 text-sand-50 font-semibold shadow-2xs"
                    : "text-charcoal-800 hover:text-forest-900 hover:bg-sand-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Burger button: visible at < 1200px */}
          <div className="min-[1200px]:hidden flex items-center gap-2">
            <Link
              to="/kontakt"
              className="px-3 py-1.5 text-xs font-semibold rounded-md bg-forest-800 text-white cursor-pointer mr-1"
            >
              Kontakt
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
              className="p-2.5 rounded-xl bg-sand-100 text-forest-900 hover:bg-sand-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest-700"
              aria-label="Åbn menu"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-sand-200 bg-sand-50 px-4 pt-3 pb-6 space-y-1 animate-in fade-in slide-in-from-top-2 duration-200 shadow-xl">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-3.5 py-2.5 text-base font-medium rounded-lg transition-colors cursor-pointer ${
                isActive(item.path)
                  ? "bg-forest-900 text-sand-50 font-semibold shadow-xs"
                  : "text-charcoal-800 hover:bg-sand-100"
              }`}
            >
              {item.label}
            </Link>
          ))}

          <div className="pt-4 mt-3 border-t border-sand-200 flex flex-col gap-2">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="w-full py-2.5 px-4 bg-sand-200 hover:bg-sand-300 text-forest-900 text-center text-sm font-semibold rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone text-emerald-700"></i>
              <span className="whitespace-nowrap">{COMPANY.phone}</span>
            </a>
            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full py-3 px-4 bg-forest-900 hover:bg-forest-800 text-white text-center text-sm font-bold rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2 shadow-sm"
            >
              <i className="fa-solid fa-envelope"></i>
              <span>Send en besked / Book tid</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
